import { Component, HostListener, inject, OnInit } from '@angular/core';
import { ModalService } from 'src/app/services/modal.service';
import { GTM } from 'src/app/services/gtm.service';
import { environment } from 'src/environments/environment';
import { BasketConfirmationData } from './models/basket-confirmation-data';

/**
 * Modale de confirmation affichée après un ajout au panier réussi (client uniquement).
 *
 * Présente le titre du projet et son montant total, et propose de rejoindre le panier
 * Castorama (nouvel onglet) ou de revenir au projet. La fermeture (croix / ESC)
 * ne déclenche aucune navigation. La touche ESC est gérée localement : le listener n'existe
 * que pendant que la modale est affichée, sans effet de bord sur les autres modales.
 */
@Component({
  selector: 'ng-casto-basket-confirmation',
  templateUrl: './basket-confirmation.component.html',
  styleUrl: './basket-confirmation.component.scss',
})
export class BasketConfirmationComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly gtm = inject(GTM);

  protected projectName = '';
  protected totalAmount = 0;
  /** Passe à true si la redirection vers le panier a échoué (popup bloquée). */
  protected redirectError = false;

  /** Dimensions projet jointes à chaque event GTM, alignées sur les autres events de l'app. */
  private trackingData: Record<string, unknown> = {};

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.dismiss();
  }

  ngOnInit(): void {
    const data = this.modalService.currentDatas as BasketConfirmationData;
    this.projectName = data?.projectName ?? '';
    this.totalAmount = data?.totalAmount ?? 0;
    this.trackingData = { projectType: data?.projectType, projectId: data?.projectId };

    this.gtm.trackEvent('affichage-modale-panier', this.trackingData);
  }

  /** « Voir mon panier » : ouvre le panier e-commerce dans un nouvel onglet. */
  protected seeBasket(): void {
    this.gtm.trackEvent('clic-voir-panier', this.trackingData);

    // On garde le handle (pas de flag 'noopener' qui renverrait null) pour détecter un blocage de popup,
    // tout en neutralisant `opener` afin d'éviter le reverse-tabnabbing.
    const opened = window.open(environment.links.basketUrl, '_blank');
    if (opened) {
      opened.opener = null;
    }

    // window.open renvoie null si un bloqueur de popup a empêché l'ouverture : on reste sur la modale.
    this.redirectError = !opened;
  }

  /** « Retour au projet » : ferme la modale sans navigation. */
  protected backToProject(): void {
    this.gtm.trackEvent('clic-retour-projet', this.trackingData);
    this.modalService.close();
  }

  /** Fermeture via la croix : tracée puis fermée, sans navigation. */
  protected dismiss(): void {
    this.gtm.trackEvent('fermeture-modale-panier', this.trackingData);
    this.modalService.close();
  }
}
